import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

// Defensive guard: the account-info payload we receive from the host app
// (JazelAppContainer/JAMS) can, in some race conditions, hand us a `juid`
// with the JS primitive `undefined` stringified and appended to it (e.g.
// "AXALWE8539undefined" instead of "AXALWE8539"). Every API call in this app
// funnels the account id through `setAccountId`, so sanitizing it once here
// stops the malformed value from ever reaching a `jazelId=...` query param.
const UNDEFINED_SUFFIX_RE = /undefined$/i

export const sanitizeAccountId = (value: unknown): string => {
  const trimmed = String(value ?? '').trim()
  if (trimmed.toLowerCase() === 'undefined') return ''
  return trimmed.replace(UNDEFINED_SUFFIX_RE, '')
}

export const useAccountSettingsStore = defineStore('accountSettings', () => {
  const accountId = ref('')
  const isClientUser = ref(false)
  const sessionToken = ref('')
  const userRole = ref('')
  const inventoryIds = ref<number[]>([])
  const accountSettings = ref<IAccountSettings | null>(null)
  // legacy flag removed: isAccountTemplatesInitialized

  const dealerTimeZone = computed(() => {
    const s: any = accountSettings.value as any
    const tz = String(s?.timezone || s?.config?.timezone || '').trim()
    return tz
  })

  const setAccountSettings = (settings: IAccountSettings | null) => {
    // Set Account ID
    setAccountId(settings?.juid ?? '')

    // Set Inventory IDs
    const inventoryIds = settings?.a5Account ?? []
    const ensuredArrayOfIds = Array.isArray(inventoryIds)
      ? inventoryIds
      : String(inventoryIds)
          .split(',')
          .map((id: string) => id.trim())
    const ensuredNumberIds = ensuredArrayOfIds.map((id: string | number) => Number(id))
    setInventoryId(ensuredNumberIds)

    // Store settings
    accountSettings.value = settings
  }

  const setIsClientUser = (value: boolean) => {
    isClientUser.value = value
  }

  const setSessionToken = (token: string) => {
    sessionToken.value = token
  }

  const setUserRole = (role: unknown) => {
    userRole.value = String(role ?? '').trim()
  }

  const getAccountSettings = (): any => {
    return accountSettings.value
  }

  const setAccountId = (newAccountId: string) => {
    accountId.value = sanitizeAccountId(newAccountId)
  }

  const setInventoryId = (newInventoryIds: number[]) => {
    inventoryIds.value = newInventoryIds
  }

  return {
    accountId,
    inventoryIds,
    isClientUser,
    sessionToken,
    userRole,
    dealerTimeZone,

    getAccountSettings,
    setAccountSettings,
    setIsClientUser,
    setSessionToken,
    setUserRole,
    setAccountId,
  }
})
